import { useEffect, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { CONTENT_PLATFORM_PROPERTY_NAME, getContentPropertyValue, useContentObjectType } from 'content-management-data-lib';
import http from 'hub-http/clients/apiClient';
import { CONTENT_ACTION_IDS } from '../constants/actionIds';
import { useContentActions } from '../providers/ContentActionsProvider';
import { getContentIds } from '../utils/contentUtils';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { jsx as _jsx } from "react/jsx-runtime";
const ViewBulkUpdateErrorsList = ({
  onViewErrorsClick
}) => {
  return /*#__PURE__*/_jsx(Button, {
    use: "transparent",
    onClick: onViewErrorsClick,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "content-management-actions-lib.modals.viewAllBulkActionErrors"
    })
  });
};
const useContentBulkUpdateAction = ({
  crmObjects,
  actionId,
  actionPath,
  asyncActionType,
  closeModal,
  baseRequestPath,
  customActionSuccessHandler
}) => {
  const {
    contentObjectType
  } = useContentObjectType();
  const [bulkUpdatePollingStatusLink, setBulkUpdatePollingStatusLink] = useState();
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [bulkUpdateProgressValue, setBulkUpdateProgressValue] = useState(0);
  const [bulkUpdatePollingInProgress, setBulkUpdatePollingInProgress] = useState(false);
  const [bulkUpdateData, setBulkUpdateData] = useState({
    completedResults: [],
    failedResults: []
  });
  const [bulkActionReconciliationData, setBulkActionReconciliationData] = useState({});
  const {
    onActionError,
    onActionSuccess,
    setIsBulkUpdateErrorsModalOpen,
    setBulkUpdateErrorsData
  } = useContentActions();
  const handleBulkUpdateActionConfirm = ({
    actionPayload = {},
    reconciliationData = {}
  }) => {
    setRequestInProgress(true);
    setBulkActionReconciliationData(Object.assign({}, actionPayload, reconciliationData));
    const asyncApiRequestUrl = baseRequestPath ? `${baseRequestPath}/async` : `content-management/${asyncActionType}/v1/${contentObjectType}/bulk/${actionPath}/async`;
    http.post(asyncApiRequestUrl, {
      data: Object.assign({
        objectIds: getContentIds(crmObjects)
      }, actionPayload)
    }).then(({
      id
    }) => {
      setBulkUpdatePollingStatusLink(`${asyncApiRequestUrl}/tasks/${id}/status`);
      setBulkUpdatePollingInProgress(true);
    }).catch(error => {
      setRequestInProgress(false);
      onActionError({
        actionId,
        error,
        data: {
          failedResults: crmObjects
        }
      });
    });
  };
  useEffect(() => {
    if (requestInProgress && bulkUpdatePollingStatusLink && bulkUpdateProgressValue < 100 && bulkUpdatePollingInProgress) {
      setTimeout(() => {
        setBulkUpdatePollingInProgress(false);
        http.get(bulkUpdatePollingStatusLink).then(({
          status,
          results
        }) => {
          const data = results.reduce((acc, {
            objectId,
            bulkActionState
          }) => {
            const crmObject = crmObjects.find(c => getContentPropertyValue(c, CONTENT_PLATFORM_PROPERTY_NAME.ID) === objectId);
            if (bulkActionState.bulkActionStateType === 'DONE') {
              acc.completedResults.push({
                crmObject,
                bulkActionState
              });
            } else if (bulkActionState.bulkActionStateType === 'ERROR') {
              acc.failedResults.push({
                crmObject,
                bulkActionState
              });
            }
            return acc;
          }, {
            completedResults: [],
            failedResults: []
          });
          setBulkUpdateData(data);
          const progressValue = data.completedResults.length / crmObjects.length * 100;
          setBulkUpdateProgressValue(progressValue);
          if (status === 'COMPLETE') {
            setRequestInProgress(false);
            setBulkUpdatePollingInProgress(false);
          } else {
            setBulkUpdatePollingInProgress(true);
          }
        }).catch(error => {
          setRequestInProgress(false);
          setBulkUpdatePollingStatusLink(undefined);
          const failedCrmObjects = bulkUpdateData.failedResults.map(({
            crmObject
          }) => crmObject);
          onActionError({
            actionId,
            error,
            data: {
              failedResults: failedCrmObjects
            }
          });
        });
      }, 2000);
    }
  }, [actionId, bulkUpdatePollingInProgress, bulkUpdatePollingStatusLink, bulkUpdateProgressValue, crmObjects, onActionError, requestInProgress, bulkUpdateData.failedResults]);
  useEffect(() => {
    if (!requestInProgress && bulkUpdatePollingStatusLink) {
      setBulkUpdatePollingStatusLink(undefined);
      if (bulkUpdateData.completedResults.length > 0) {
        const completedCrmObjects = bulkUpdateData.completedResults.map(({
          crmObject
        }) => crmObject);
        const successData = {
          completedResults: completedCrmObjects,
          actionData: bulkActionReconciliationData
        };
        if (customActionSuccessHandler) {
          customActionSuccessHandler(successData);
        } else {
          onActionSuccess({
            actionId,
            data: successData
          });
          closeModal();
        }
      }
      if (bulkUpdateData.failedResults.length > 0) {
        const failedCrmObjects = bulkUpdateData.failedResults.map(({
          crmObject
        }) => crmObject);
        onActionError({
          actionId,
          data: {
            failedResults: failedCrmObjects
          },
          bulkActionErrorDetailsModalButton: actionId === CONTENT_ACTION_IDS.PUBLISHING_OPTIONS ? /*#__PURE__*/_jsx(ViewBulkUpdateErrorsList, {
            onViewErrorsClick: () => {
              setBulkUpdateErrorsData({
                actionId,
                results: bulkUpdateData.failedResults
              });
              setIsBulkUpdateErrorsModalOpen(true);
              closeModal();
              Object.keys(FloatingAlertStore.getAlerts()).forEach(k => {
                FloatingAlertStore.removeAlert(k);
              });
            }
          }) : undefined
        });
      }
    }
  }, [actionId, bulkActionReconciliationData, bulkUpdateData.completedResults, bulkUpdateData.completedResults.length, bulkUpdateData.failedResults, bulkUpdateData.failedResults.length, bulkUpdatePollingStatusLink, closeModal, crmObjects.length, onActionError, onActionSuccess, requestInProgress, setBulkUpdateErrorsData, setIsBulkUpdateErrorsModalOpen, customActionSuccessHandler]);
  return {
    requestInProgress,
    bulkUpdateProgressValue,
    handleBulkUpdateActionConfirm
  };
};
export default useContentBulkUpdateAction;